// i18n: translated
/**
 * ヘッダー右上の「自分のタスク」アイコン + 未完了件数バッジ (steering 627、高橋 2026-09-15)
 *
 * 自分が担当の未完了タスク数をバッジで出し、押すとタスク管理の「自分の担当 (未完了)」へ移る。
 * `tasks:read` が無ければ出さない (sidebar の「タスク管理」と同じ gate。API も呼ばない)。
 * SPEC 7.1.0s 準拠。
 */
import { Link } from 'react-router-dom';
import { useT } from '@/i18n';
import { shellNs } from '@/i18n/namespaces/shell';
import { usePermission } from '@/hooks/usePermission';
import { MY_OPEN_TASK_BADGE_MAX, useMyOpenTaskCount } from '@/hooks/useMyOpenTaskCount';

/** 遷移先: タスク管理を「自分の担当」で開く (状態は既定の未完了)。 */
export const MY_TASKS_LINK = '/client-tasks?scope=assigned_to_me';

export default function MyTasksBadge() {
  const { canRead } = usePermission();
  return canRead('tasks') ? <AuthorizedMyTasksBadge /> : null;
}

function AuthorizedMyTasksBadge() {
  const t = useT(shellNs);
  const { count, overflow } = useMyOpenTaskCount(true);
  const capped = count !== null && (overflow || count > MY_OPEN_TASK_BADGE_MAX);
  const label =
    count === null
      ? t('myTasks.title')
      : capped
        ? t('myTasks.countOver', { max: MY_OPEN_TASK_BADGE_MAX })
        : t('myTasks.count', { count });

  return (
    <Link
      to={MY_TASKS_LINK}
      className="relative rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
      aria-label={label}
      title={t('myTasks.title')}
      data-testid="header-my-tasks"
    >
      {/* Heroicons: clipboard-document-check (案件詳細ヘッダーの「タスク」導線と同じ) */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className="h-5 w-5"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75"
        />
      </svg>

      {/* 未完了件数 (0 件は出さない。通知ベルの未読バッジと同じ位置・大きさ、色は brand の indigo) */}
      {count !== null && count > 0 && (
        <span
          data-testid="header-my-tasks-count"
          className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-indigo-500 px-1 text-[10px] font-bold text-white"
        >
          {capped ? `${MY_OPEN_TASK_BADGE_MAX}+` : count}
        </span>
      )}
    </Link>
  );
}
